import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { postBedOccupant, patchBedOccupant } from "../../services/httpClient";
import { HousingCandidate, CandidateRow, bedDisplayName, buildPersonCandidateRows } from "../../helpers/housingAssignment";
import CandidateSearchPanel from "./CandidateSearchPanel";
import BedRow from "../ui/BedRow";
import InlineConfirm from "../ui/InlineConfirm";

interface BedAssignmentRowState {
  bed: any;
  beds: any[];
  rooms: any[];
  facilities: any[];
  possibleOccupants: any[];
  housingByCaseId: Map<number, HousingCandidate>;
  onAssigned: any;
  onPickerOpenChange: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const BedAssignmentRow = ({
  bed,
  beds,
  rooms,
  facilities,
  possibleOccupants,
  housingByCaseId,
  onAssigned,
  onPickerOpenChange,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: BedAssignmentRowState) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmVacate, setConfirmVacate] = useState(false);

  const occupied = !!bed.occupant_id;
  const bedLabel = bedDisplayName(bed, rooms, facilities);

  const onError = (err: any) => {
    setErrMessage(err?.response?.data?.msg);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const onClickVacate = () => {
    setErrMessage("");
    patchBedOccupant(bed.case_id, bed.bed_occupant_id)
      .then(() => {
        setShowToast(true);
        setToastText("Bed Vacated");
        setToastBody(bedLabel);
        setConfirmVacate(false);
        onAssigned();
      })
      .catch(onError);
  };

  // The parent counts open pickers to lock its filters, so an unmount while one is open -
  // collapsing the facility, say - would leave that count stranded above zero forever.
  const pickerOpenRef = useRef(false);
  useEffect(() => {
    pickerOpenRef.current = pickerOpen;
  }, [pickerOpen]);
  useEffect(
    () => () => {
      if (pickerOpenRef.current) onPickerOpenChange(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onClickAssign = () => {
    setErrMessage("");
    setSearch("");
    // not inside the updater: it must stay pure, and React may run it more than once
    const next = !pickerOpen;
    setPickerOpen(next);
    onPickerOpenChange(next);
  };

  const onPickPerson = (row: CandidateRow) => {
    const person = row.candidate;
    const body: any = { bed_id: bed.id };
    if (person.type === "guardian") {
      body.user_id = person.id;
    } else {
      body.member_id = person.id;
    }

    setErrMessage("");
    postBedOccupant(body, person.case_id)
      .then(() => {
        setShowToast(true);
        setToastText("Bed Assigned");
        setToastBody(`${person.name} — ${bedLabel}`);
        setPickerOpen(false);
        onPickerOpenChange(false);
        onAssigned();
      })
      .catch(onError);
  };

  const confirming = occupied && confirmVacate;

  return (
    <BedRow
      bed={bed}
      question={confirming ? `End ${bed.firstname} ${bed.lastname}'s stay here?` : undefined}
      action={
        confirming ? (
          <InlineConfirm onConfirm={onClickVacate} onCancel={() => setConfirmVacate(false)} />
        ) : (
          <Button
            size="sm"
            className={`flex-shrink-0 flex-grow-0 ${occupied ? "app-danger-color button" : pickerOpen ? "app-primary-color button" : "app-secondary-color button"}`}
            onClick={occupied ? () => setConfirmVacate(true) : onClickAssign}
          >
            {occupied ? "Vacate" : pickerOpen ? "Cancel" : "Assign"}
          </Button>
        )
      }
    >
      {pickerOpen && (
        <CandidateSearchPanel
          placeholder="Search cases or people"
          search={search}
          onSearchChange={setSearch}
          rows={buildPersonCandidateRows(possibleOccupants, housingByCaseId, { search, beds, rooms, facilities })}
          onPick={onPickPerson}
          emptyText="No people match this search."
        />
      )}
    </BedRow>
  );
};

export default BedAssignmentRow;
